import type { FunctionMeta } from '../function.model';

/**
 * Cubic function in general/standard form: f(x) = ax³ + bx² + cx + d
 *
 * - a: cubic coefficient (stretch/compression and reflection)
 * - b: quadratic coefficient
 * - c: linear coefficient
 * - d: constant term (y-intercept)
 */
export interface CubicGeneralParams {
  a: number;
  b: number;
  c: number;
  d: number;
}

/**
 * Convert general form parameters (a, b, c, d) to inflection point (h, k).
 *
 * h = -b / (3a)
 * k = f(h) = -2ah³ + ch + d
 */
export function generalToInflection(a: number, b: number, c: number, d: number): { h: number; k: number } {
  const h = -b / (3 * a);
  const k = -2 * a * Math.pow(h, 3) + c * h + d;
  return { h, k };
}

export const CUBIC_GENERAL_META: FunctionMeta<CubicGeneralParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'extrema',
    'tangent',
    'secant',
    'derivative',
    'inflection',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'domain',
    'range',
  ],
  handleConfigs: {
    translate: {
      xParam: 'b',
      yParam: 'd',
      toPosition: (p) => {
        const h = -p['b'] / (3 * p['a']);
        const k = -2 * p['a'] * Math.pow(h, 3) + p['c'] * h + p['d'];
        return [h, k];
      },
      fromPosition: (x, y, p) => ({
        b: -3 * p['a'] * x,
        d: y + 2 * p['a'] * Math.pow(x, 3) - p['c'] * x,
      }),
    },
    'translate-x': {
      xParam: 'b',
      yParam: 'd',
      toPosition: (p) => {
        const h = -p['b'] / (3 * p['a']);
        const k = -2 * p['a'] * Math.pow(h, 3) + p['c'] * h + p['d'];
        return [h, k];
      },
      fromPosition: (x, _y, p) => {
        const currentH = -p['b'] / (3 * p['a']);
        const currentK = -2 * p['a'] * Math.pow(currentH, 3) + p['c'] * currentH + p['d'];
        return {
          b: -3 * p['a'] * x,
          d: currentK + 2 * p['a'] * Math.pow(x, 3) - p['c'] * x,
        };
      },
    },
    'translate-y': {
      xParam: 'b',
      yParam: 'd',
      toPosition: (p) => {
        const h = -p['b'] / (3 * p['a']);
        const k = -2 * p['a'] * Math.pow(h, 3) + p['c'] * h + p['d'];
        return [h, k];
      },
      fromPosition: (_x, y, p) => {
        const currentH = -p['b'] / (3 * p['a']);
        return {
          b: -3 * p['a'] * currentH,
          d: y + 2 * p['a'] * Math.pow(currentH, 3) - p['c'] * currentH,
        };
      },
    },
  },
  defaultParams: {
    a: 1,
    b: 0,
    c: 0,
    d: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [-20, 20],
    c: [-20, 20],
    d: [-20, 20],
  },
};
